import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Award, Download, Eye, Filter } from "lucide-react";
import ReportCardWall from "../components/ReportCardWall";
import Panda from "../components/Panda";
import GoalBadge from "../components/GoalBadge";
import { goalStreakWeeks, goalBadge, GOAL_TIERS } from "../lib/goalStreak";
import { CurveBottom, Cloud } from "../components/Decor";
import { modules } from "../data/mock";
import { useProgress, progressPercent } from "../context/ProgressContext";

const CertificateGallery = () => {
  const { children, followedChildren } = useProgress();
  const all = [...children, ...followedChildren];
  const [filter, setFilter] = useState("all");

  const certs = all.flatMap((child) =>
    modules
      .filter((m) => progressPercent(child, m.id) === 100)
      .map((m) => ({ child, m, date: child.completed?.[m.id]?.certifiedAt ? new Date(child.completed[m.id].certifiedAt) : null, quiz: child.completed?.[m.id]?.quiz }))
  ).sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

  const shown = filter === "all" ? certs : certs.filter((c) => c.child.id === filter);
  const badged = all.filter((c) => c.weeklyGoal && (filter === "all" || c.id === filter));
  const withBadge = badged.filter((c) => goalBadge(goalStreakWeeks(c.stepLog, c.weeklyGoal)));

  return (
    <div data-testid="certificate-gallery">
      <section className="relative bg-[#f3c932] overflow-hidden">
        <Cloud className="absolute right-[8%] top-6 hidden md:block cloud-drift" size={170} />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-24 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1 text-center md:text-left">
            <p className="text-[#8a6d00] font-bold uppercase tracking-widest text-sm">Hall of fame</p>
            <h1 className="text-white font-bold text-4xl sm:text-5xl mt-2 drop-shadow-[0_2px_0_rgba(0,0,0,0.2)]" style={{ fontFamily: "Literata, serif" }}>Certificate wall</h1>
            <p className="text-[#5a4600] text-lg mt-3 font-medium">Every lesson finished, every promise kept. {certs.length} certificate{certs.length === 1 ? "" : "s"} earned so far.</p>
          </div>
          <Panda size={150} accessory="crown" scarf="#e5356f" item="coin" animate />
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <section className="bg-[#f7fdfe] pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {all.length > 1 && (
            <div className="-mt-6 relative z-10 bg-white rounded-full shadow-lg p-1.5 inline-flex flex-wrap items-center gap-1" data-testid="gallery-filter">
              <span className="px-3 text-gray-400"><Filter size={16} /></span>
              <button onClick={() => setFilter("all")} className={`rounded-full px-4 py-2 text-sm font-bold ${filter === "all" ? "bg-[#1b8bc9] text-white" : "text-[#0c4a6e] hover:bg-[#e9f9fd]"}`}>Everyone</button>
              {all.map((c) => (
                <button key={c.id} onClick={() => setFilter(c.id)} className={`rounded-full pl-1 pr-4 py-1 text-sm font-bold inline-flex items-center gap-1 ${filter === c.id ? "bg-[#1b8bc9] text-white" : "text-[#0c4a6e] hover:bg-[#e9f9fd]"}`} data-testid={`gallery-filter-${c.id}`}>
                  <Panda size={26} accessory={c.panda.accessory} scarf={c.panda.scarf} /> {c.panda.name}
                </button>
              ))}
            </div>
          )}

          {all.some((c) => c.ownerId) && <ReportCardWall children={all.filter((c) => c.ownerId && (filter === "all" || c.id === filter))} />}

          {badged.length > 0 && (
            <div className="mt-10 bg-white rounded-3xl shadow-md p-6 sm:p-8" data-testid="goal-badges-section">
              <h2 className="text-xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }}>Goal streak badges</h2>
              <p className="text-sm text-gray-600 mt-1">Hit the weekly lesson goal 4 weeks in a row for Bronze, 8 for Silver, 12 for Gold. {withBadge.length === 0 && "Nobody has a badge yet - keep the streak going!"}</p>
              <div className="mt-4 flex flex-wrap gap-4">
                {badged.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-2xl bg-[#f7fdfe] px-4 py-3" data-testid={`goal-badge-card-${c.id}`}>
                    <Panda size={40} accessory={c.panda.accessory} scarf={c.panda.scarf} />
                    <div>
                      <p className="font-bold text-[#0c4a6e]">{c.panda.name}</p>
                      <GoalBadge child={c} size="lg" showProgress />
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {[...GOAL_TIERS].reverse().filter((t) => goalStreakWeeks(c.stepLog, c.weeklyGoal) >= t.minWeeks).map((t) => (
                          <Link key={t.tier} to={`/badge-certificate/${c.id}/${t.tier}`} className="inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1 border-2 hover:bg-white" style={{ color: t.color, borderColor: t.color, background: t.bg }} data-testid={`badge-cert-link-${c.id}-${t.tier}`}><Award size={12} /> {t.label} certificate</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {shown.length === 0 ? (
            <div className="mt-10 bg-white rounded-3xl shadow-md p-12 text-center" data-testid="gallery-empty">
              <Panda size={130} accessory="hat" scarf="#1b8bc9" className="mx-auto" />
              <h2 className="text-2xl font-bold text-[#0c4a6e] mt-4" style={{ fontFamily: "Literata, serif" }}>No certificates yet</h2>
              <p className="text-gray-600 mt-2">Finish all six steps of any lesson to hang the first one here.</p>
              <Link to="/lessons" className="mt-6 inline-block btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white">Go to lessons</Link>
            </div>
          ) : (
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {shown.map(({ child, m, date, quiz }) => (
                <div key={`${child.id}-${m.id}`} className="group bg-white rounded-3xl shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300 overflow-hidden" data-testid={`gallery-cert-${child.id}-${m.id}`}>
                  <div className="relative p-5 pb-0">
                    <div className="rounded-2xl border-4 p-4 text-center relative" style={{ borderColor: m.color, background: `${m.color}14` }}>
                      <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-gray-500">Certificate of achievement</p>
                      <div className="flex items-center justify-center gap-3 mt-2">
                        <Panda size={64} accessory="crown" scarf={child.panda.scarf} />
                        <p className="font-logo text-3xl" style={{ color: m.button }}>{child.panda.name}</p>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">completed Lesson {m.num}</p>
                      <p className="font-bold text-[#0c4a6e] text-sm leading-snug" style={{ fontFamily: "Literata, serif" }}>{m.title}</p>
                      <span className="absolute -top-3 -right-3 w-9 h-9 rounded-full text-white flex items-center justify-center shadow" style={{ background: m.button }}><Award size={18} /></span>
                    </div>
                  </div>
                  <div className="p-5 flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500">
                      <p>{date ? date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "Earned"}{quiz !== undefined && ` · Quiz ${quiz}/5`}</p>
                      {child.ownerName && <p className="text-[#7d5fb2] font-bold">{child.ownerName}'s family</p>}
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/certificate/${m.slug}?child=${child.id}`} className="w-9 h-9 rounded-full bg-[#e9f9fd] text-[#1b8bc9] flex items-center justify-center hover:bg-[#1b8bc9] hover:text-white transition-colors" aria-label="View" data-testid={`gallery-view-${child.id}-${m.id}`}><Eye size={16} /></Link>
                      <Link to={`/certificate/${m.slug}?child=${child.id}&download=1`} className="w-9 h-9 rounded-full bg-[#e9f9fd] text-[#1b8bc9] flex items-center justify-center hover:bg-[#1b8bc9] hover:text-white transition-colors" aria-label="Download" data-testid={`gallery-download-${child.id}-${m.id}`}><Download size={16} /></Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CertificateGallery;
